/**
 * Chart-specific glyphs — drawing-tool overlays (trend/ray/segment lines,
 * Fibonacci tools, Gann box, Elliott wave, ABCD, shapes) and app-section
 * icons (Screener, Watchlist, Fundamentals). Stay hand-drawn permanently:
 * no icon library covers technical-analysis overlay shapes, and the
 * section icons are this app's own visual identity, not generic UI chrome.
 *
 * Contract for every icon below: 24x24 viewBox, strokeWidth 2 on primary
 * strokes (1 + dasharray "3 2" for secondary/guide lines only), round
 * cap+join, currentColor. Keep new icons consistent with this — see
 * ui.tsx's header for why these can't just move to the icon library.
 */
import type { IconProps } from './types';

export function TrendLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="4" y1="20" x2="20" y2="4"/>
      <circle cx="4" cy="20" r="2" fill="currentColor"/>
      <circle cx="20" cy="4" r="2" fill="currentColor"/>
    </svg>
  );
}

export function HorizontalLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="2" y1="12" x2="22" y2="12"/>
    </svg>
  );
}

export function FibonacciIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="2" y1="5" x2="22" y2="5"/>
      <line x1="2" y1="10" x2="22" y2="10"/>
      <line x1="2" y1="15" x2="22" y2="15"/>
      <line x1="2" y1="19" x2="22" y2="19"/>
      <line x1="4" y1="4" x2="4" y2="20" strokeWidth="1" strokeDasharray="3 2"/>
      <line x1="20" y1="4" x2="20" y2="20" strokeWidth="1" strokeDasharray="3 2"/>
    </svg>
  );
}

export function VerticalLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="12" y1="2" x2="12" y2="22"/>
    </svg>
  );
}

export function RayLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="4" y1="20" x2="19" y2="5"/>
      <circle cx="4" cy="20" r="2" fill="currentColor" stroke="none"/>
      <polyline points="13,5 19,5 19,11"/>
    </svg>
  );
}

export function HorizontalRayLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <circle cx="3" cy="12" r="2" fill="currentColor" stroke="none"/>
      <polyline points="16,7 21,12 16,17"/>
    </svg>
  );
}

export function VerticalRayLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="12" y1="21" x2="12" y2="3"/>
      <circle cx="12" cy="21" r="2" fill="currentColor" stroke="none"/>
      <polyline points="7,8 12,3 17,8"/>
    </svg>
  );
}

export function SegmentLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="5" y1="19" x2="19" y2="5"/>
      <circle cx="5" cy="19" r="2" fill="currentColor" stroke="none"/>
      <circle cx="19" cy="5" r="2" fill="currentColor" stroke="none"/>
    </svg>
  );
}

export function HorizontalSegmentIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="4" y1="12" x2="20" y2="12"/>
      <circle cx="4" cy="12" r="2" fill="currentColor" stroke="none"/>
      <circle cx="20" cy="12" r="2" fill="currentColor" stroke="none"/>
    </svg>
  );
}

export function VerticalSegmentIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="12" y1="4" x2="12" y2="20"/>
      <circle cx="12" cy="4" r="2" fill="currentColor" stroke="none"/>
      <circle cx="12" cy="20" r="2" fill="currentColor" stroke="none"/>
    </svg>
  );
}

export function PriceLineIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="2" y1="12" x2="15" y2="12"/>
      <rect x="15" y="8" width="7" height="8" rx="1"/>
    </svg>
  );
}

export function ParallelLinesIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="3" y1="17" x2="17" y2="7"/>
      <line x1="7" y1="21" x2="21" y2="11"/>
    </svg>
  );
}

export function PriceChannelIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="3" y1="7" x2="21" y2="7"/>
      <line x1="3" y1="17" x2="21" y2="17"/>
      <line x1="3" y1="7" x2="21" y2="17" strokeWidth="1" strokeDasharray="3 2"/>
    </svg>
  );
}

export function AnnotationIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

export function TextBoxIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="16" rx="1"/>
      <line x1="7" y1="9" x2="17" y2="9"/>
      <line x1="7" y1="14" x2="14" y2="14"/>
    </svg>
  );
}

export function MenuRightIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="15" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}

export function BookmarkPlusIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
      <line x1="12" y1="7" x2="12" y2="13"/>
      <line x1="9" y1="10" x2="15" y2="10"/>
    </svg>
  );
}

export function ScreenerIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  );
}

// Table-with-sparkline icon — visually distinct from ScreenerIcon (funnel).
// Represents the new StockScreener v2 with RS sparklines.
export function Screener2Icon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      {/* Table rows */}
      <rect x="2" y="3" width="20" height="18" rx="2"/>
      <line x1="2" y1="8" x2="22" y2="8"/>
      <line x1="2" y1="13" x2="22" y2="13"/>
      <line x1="2" y1="18" x2="22" y2="18"/>
      {/* Sparkline in top-right cell */}
      <polyline points="14 5 16.5 6.5 18.5 5.5 21 4.5" strokeWidth="1.5"/>
    </svg>
  );
}

export function ArrowIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="5" y1="19" x2="19" y2="5"/>
      <polyline points="8 5 19 5 19 16"/>
    </svg>
  );
}

export function RectIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="1"/>
    </svg>
  );
}

export function CircleIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <circle cx="12" cy="12" r="9"/>
    </svg>
  );
}

export function TriangleIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polygon points="12 3 22 21 2 21"/>
    </svg>
  );
}

export function ParallelogramIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polygon points="6 18 18 18 18 6 6 6"/>
      <line x1="6" y1="18" x2="4" y2="6"/>
      <line x1="20" y1="18" x2="18" y2="6"/>
    </svg>
  );
}

export function FibExtensionIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="11" x2="21" y2="11"/>
      <line x1="3" y1="16" x2="21" y2="16"/>
      <line x1="8" y1="3" x2="8" y2="21" strokeWidth="1" strokeDasharray="3 2"/>
      <line x1="16" y1="3" x2="16" y2="21" strokeWidth="1" strokeDasharray="3 2"/>
    </svg>
  );
}

export function WatchlistIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="4" rx="1"/>
      <rect x="3" y="10" width="18" height="4" rx="1"/>
      <rect x="3" y="16" width="18" height="4" rx="1"/>
    </svg>
  );
}

export function FibFanIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="3" y1="21" x2="21" y2="3"/>
      <line x1="3" y1="21" x2="21" y2="9"/>
      <line x1="3" y1="21" x2="21" y2="15"/>
      <line x1="3" y1="3" x2="3" y2="21"/>
    </svg>
  );
}

export function LayoutIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="12" y1="9" x2="12" y2="21"/>
    </svg>
  );
}

export function FibCircleIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="12" cy="12" r="8"/>
    </svg>
  );
}

export function FibSegmentIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="4" y1="5" x2="20" y2="5"/>
      <line x1="4" y1="10" x2="20" y2="10"/>
      <line x1="4" y1="15" x2="20" y2="15"/>
      <line x1="4" y1="20" x2="20" y2="20"/>
    </svg>
  );
}

export function FibSpiralIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <path d="M12 12 Q12 4 20 4 Q20 20 4 20 Q4 8 14 8 Q14 16 8 16"/>
    </svg>
  );
}

export function GannBoxIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="12" y1="3" x2="12" y2="21"/>
      <line x1="3" y1="3" x2="21" y2="21" strokeWidth="1" strokeDasharray="3 2"/>
    </svg>
  );
}

export function WaveIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polyline points="3 17 7 7 12 15 17 5 21 12"/>
    </svg>
  );
}

export function AbcdIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polyline points="4 18 8 6 14 15 20 4"/>
      <line x1="4" y1="18" x2="14" y2="15" strokeWidth="1" strokeDasharray="3 2"/>
      <line x1="8" y1="6" x2="20" y2="4" strokeWidth="1" strokeDasharray="3 2"/>
    </svg>
  );
}

export function CurveIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M3 17 C6 17 6 7 12 7 C18 7 18 17 21 17"/>
    </svg>
  );
}

export function IndicatorIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <polyline points="7 17 10 13 13 15 17 9"/>
    </svg>
  );
}

/** Two parallel horizontal lines — represents support/resistance levels */
export function SupportResistanceIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <line x1="3" y1="8" x2="21" y2="8"/>
      <line x1="3" y1="16" x2="21" y2="16"/>
      <polyline points="8 5 12 8 16 5"/>
      <polyline points="8 19 12 16 16 19"/>
    </svg>
  );
}

/** Zigzag breakout arrow — represents a breakout detection */
export function BreakoutIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polyline points="3 17 8 12 13 15 18 8"/>
      <polyline points="15 8 18 8 18 11"/>
    </svg>
  );
}

/** Bullseye target — represents price targets */
export function PriceTargetIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  );
}

/** Bar chart icon — represents full chart summary */
export function ChartSummaryIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}

export function ResetViewIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M3 3h5v5H3z" fill="currentColor" stroke="none" opacity="0.3"/>
      <path d="M16 3h5v5h-5z" fill="currentColor" stroke="none" opacity="0.3"/>
      <path d="M3 16h5v5H3z" fill="currentColor" stroke="none" opacity="0.3"/>
      <path d="M16 16h5v5h-5z" fill="currentColor" stroke="none" opacity="0.3"/>
      <polyline points="3 8 3 3 8 3"/>
      <polyline points="16 3 21 3 21 8"/>
      <polyline points="21 16 21 21 16 21"/>
      <polyline points="8 21 3 21 3 16"/>
    </svg>
  );
}

// Balance-sheet / fundamentals analysis icon — horizontal rows with a rising bar overlay
export function FundamentalsIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      {/* Document outline */}
      <rect x="3" y="2" width="18" height="20" rx="2"/>
      {/* Horizontal lines (rows) */}
      <line x1="7" y1="7" x2="17" y2="7"/>
      <line x1="7" y1="11" x2="17" y2="11"/>
      <line x1="7" y1="15" x2="13" y2="15"/>
      {/* Rising bar chart overlay (bottom-right) */}
      <rect x="13" y="17" width="2" height="4" fill="currentColor" stroke="none" opacity="0.7"/>
      <rect x="16" y="14" width="2" height="7" fill="currentColor" stroke="none" opacity="0.7"/>
    </svg>
  );
}
